import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ClipboardText, UserCheck, UserMinus, Clock, CurrencyInr,
  SignIn, SignOut, CalendarBlank, Funnel, DownloadSimple,
  PencilSimple, Trash, Warning, Check, CaretLeft, CaretRight, CalendarDots, UsersThree
} from "@phosphor-icons/react";

const API = `${window.location.origin}/api`;
const formatCurrency = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(v);
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const STATUS_CONFIG = {
  present: { label: "Present", color: "bg-green-100 text-green-700 border-green-200", dot: "bg-green-500" },
  absent: { label: "Absent", color: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
  half_day: { label: "Half Day", color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  leave: { label: "Leave", color: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  permission: { label: "Permission", color: "bg-violet-100 text-violet-700 border-violet-200", dot: "bg-violet-500" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.present;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${cfg.color}`} data-testid={`status-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

/* ====== Quick Check-In ====== */
function QuickCheckIn({ teamMembers, onDone }) {
  const [name, setName] = useState("");
  const [status, setStatus] = useState("present");
  const [permissionHours, setPermissionHours] = useState("");
  const [permissionReason, setPermissionReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCheckIn = async () => {
    if (!name) { toast.error("Select an employee"); return; }
    if (status === "permission" && (!permissionHours || parseFloat(permissionHours) <= 0)) {
      toast.error("Enter permission hours"); return;
    }
    setLoading(true);
    try {
      const payload = { employee_name: name, status };
      if (status === "permission") {
        payload.permission_hours = parseFloat(permissionHours) || 0;
        payload.permission_reason = permissionReason;
      }
      await axios.post(`${API}/attendance`, payload);
      toast.success(`${name} checked in!`);
      setName(""); setPermissionHours(""); setPermissionReason("");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Check-in failed"); }
    finally { setLoading(false); }
  };

  return (
    <Card className="p-4 sm:p-5 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="quick-checkin">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><SignIn size={16} className="text-green-600" weight="bold" /></div>
        <h3 className="text-sm font-bold text-green-800">Quick Check-In</h3>
      </div>
      <div className="space-y-2">
        <Select value={name} onValueChange={setName}>
          <SelectTrigger className="bg-white h-11 text-sm" data-testid="checkin-employee"><SelectValue placeholder="Select Employee" /></SelectTrigger>
          <SelectContent>{teamMembers.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-white h-11 text-sm flex-1" data-testid="checkin-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="present">Present</SelectItem>
              <SelectItem value="half_day">Half Day</SelectItem>
              <SelectItem value="leave">Leave</SelectItem>
              <SelectItem value="absent">Absent</SelectItem>
              <SelectItem value="permission">Permission (Hourly)</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleCheckIn} disabled={loading} className="h-11 px-6 bg-green-600 hover:bg-green-700 text-white font-bold touch-target" data-testid="checkin-btn">
            <SignIn size={18} className="mr-1" /> Check In
          </Button>
        </div>
        {status === "permission" && (
          <div className="flex gap-2 mt-1">
            <Input type="number" min="0.5" max="8" step="0.5" value={permissionHours} onChange={(e) => setPermissionHours(e.target.value)} placeholder="Hours (e.g. 2)" className="bg-white h-10 text-sm flex-1" data-testid="permission-hours" />
            <Input value={permissionReason} onChange={(e) => setPermissionReason(e.target.value)} placeholder="Reason (optional)" className="bg-white h-10 text-sm flex-1" data-testid="permission-reason" />
          </div>
        )}
      </div>
    </Card>
  );
}

/* ====== Dashboard Cards ====== */
function DashboardCards({ data, onCardClick }) {
  const cards = [
    { key: "all", label: "Total Staff", value: data.total_employees, icon: ClipboardText, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
    { key: "present", label: "Present", value: data.present, icon: UserCheck, color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
    { key: "absent", label: "Absent", value: data.absent, icon: UserMinus, color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
    { key: "leave", label: "On Leave", value: data.on_leave, icon: CalendarBlank, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
    { key: "half_day", label: "Half Day", value: data.half_day, icon: Clock, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
    { key: "permission", label: "Permission", value: data.permission, icon: Clock, color: "text-violet-700", bg: "bg-violet-50", border: "border-violet-200" },
    { key: "late", label: "Late", value: data.late_entries, icon: Warning, color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" },
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 sm:gap-3" data-testid="attendance-cards">
      {cards.map((c) => (
        <button key={c.label} onClick={() => onCardClick && onCardClick(c.key)} className={`text-left p-3 border-2 rounded-xl ${c.border} bg-white hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer`} data-testid={`att-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
          <div className="flex flex-col items-center sm:items-start gap-1">
            <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon size={15} className={c.color} weight="bold" /></div>
            <p className={`text-xl sm:text-2xl font-black ${c.color}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.value}</p>
            <p className={`text-[10px] sm:text-xs font-bold uppercase tracking-wide text-center sm:text-left ${c.color}`}>{c.label}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ====== Today's List ====== */
function TodayList({ records, onCheckout, onEdit, onDelete }) {
  return (
    <div data-testid="today-attendance">
      <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2"><Clock size={16} className="text-indigo-500" /> Today's Attendance</h3>
      {records.length === 0 ? (
        <Card className="p-6 text-center border border-gray-200 bg-white"><p className="text-sm text-gray-400">No attendance marked yet today</p></Card>
      ) : (
        <div className="space-y-2">
          {records.map((r) => (
            <Card key={r.attendance_id} className="p-3 border border-gray-200 bg-white" data-testid={`att-card-${r.attendance_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600 flex-shrink-0">{r.employee_name?.charAt(0)?.toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-gray-900 truncate">{r.employee_name}</p>
                    <div className="flex items-center flex-wrap gap-2 text-xs text-gray-500">
                      {r.check_in && <span className="text-green-600 font-medium">In: {r.check_in}</span>}
                      {r.check_out && <span className="text-red-600 font-medium">Out: {r.check_out}</span>}
                      {r.working_hours > 0 && <span className="font-mono">{r.working_hours}h</span>}
                      {r.is_late && <span className="text-orange-600 font-bold">LATE</span>}
                      {r.status === "permission" && r.permission_hours > 0 && <span className="text-violet-600 font-semibold">{r.permission_hours}h perm</span>}
                    </div>
                    {r.permission_reason && r.status === "permission" && <p className="text-[10px] text-violet-500 truncate mt-0.5">{r.permission_reason}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <StatusBadge status={r.status} />
                  {r.check_in && !r.check_out && r.status === "present" && (
                    <Button size="sm" onClick={() => onCheckout(r.attendance_id)} className="h-8 px-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold touch-target" data-testid={`checkout-${r.attendance_id}`}>
                      <SignOut size={14} className="mr-0.5" /> Out
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onEdit(r)} className="h-7 w-7 p-0" data-testid={`edit-att-${r.attendance_id}`}><PencilSimple size={14} className="text-indigo-600" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(r.attendance_id)} className="h-7 w-7 p-0" data-testid={`delete-att-${r.attendance_id}`}><Trash size={14} className="text-red-500" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ====== Calendar View ====== */
function CalendarView({ teamMembers, onRefresh }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [records, setRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayRecords, setDayRecords] = useState([]);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchMonthly = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attendance/monthly`, { params: { year, month } });
      setRecords(res.data);
    } catch (err) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { fetchMonthly(); }, [fetchMonthly]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const getDateStr = (day) => `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const getDaySummary = (day) => {
    const dateStr = getDateStr(day);
    const dayRecs = records.filter(r => r.date === dateStr);
    if (dayRecs.length === 0) return null;
    const present = dayRecs.filter(r => r.status === "present").length;
    const absent = dayRecs.filter(r => r.status === "absent").length;
    const perm = dayRecs.filter(r => r.status === "permission").length;
    return { total: dayRecs.length, present, absent, perm };
  };

  const handleDayClick = (day) => {
    const dateStr = getDateStr(day);
    setSelectedDate(dateStr);
    setDayRecords(records.filter(r => r.date === dateStr));
  };

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m); setYear(y);
  };

  const handleEditDay = (rec) => {
    setEditRecord(rec);
    setEditForm({ check_in: rec.check_in || "", check_out: rec.check_out || "", status: rec.status, notes: rec.notes || "", permission_hours: rec.permission_hours || 0, permission_reason: rec.permission_reason || "" });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/attendance/${editRecord.attendance_id}`, editForm);
      toast.success("Updated!");
      setEditRecord(null);
      fetchMonthly();
      if (selectedDate) {
        const res = await axios.get(`${API}/attendance`, { params: { date: selectedDate } });
        setDayRecords(res.data);
      }
      if (onRefresh) onRefresh();
    } catch (err) { toast.error("Update failed"); }
  };

  const handleDeleteDay = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await axios.delete(`${API}/attendance/${id}`);
      toast.success("Deleted!");
      fetchMonthly();
      if (selectedDate) {
        const res = await axios.get(`${API}/attendance`, { params: { date: selectedDate } });
        setDayRecords(res.data);
      }
      if (onRefresh) onRefresh();
    } catch (err) { toast.error("Delete failed"); }
  };

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div data-testid="calendar-view">
      {/* Month Nav */}
      <div className="flex items-center justify-between mb-3">
        <Button size="sm" variant="ghost" onClick={() => changeMonth(-1)} className="h-9 w-9 p-0"><CaretLeft size={18} /></Button>
        <h3 className="text-base font-bold text-gray-900">{MONTH_NAMES[month - 1]} {year}</h3>
        <Button size="sm" variant="ghost" onClick={() => changeMonth(1)} className="h-9 w-9 p-0"><CaretRight size={18} /></Button>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-3">
            {weekDays.map(d => <div key={d} className="text-center text-[10px] font-bold text-gray-400 py-1">{d}</div>)}
            {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const dateStr = getDateStr(day);
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selectedDate;
              const summary = getDaySummary(day);
              return (
                <button key={day} onClick={() => handleDayClick(day)}
                  className={`p-1 rounded-lg text-center min-h-[48px] sm:min-h-[56px] border transition-all touch-target
                    ${isSelected ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200" : isToday ? "border-indigo-300 bg-indigo-50/50" : "border-gray-100 hover:border-gray-300 hover:bg-gray-50"}
                  `}
                  data-testid={`cal-day-${day}`}
                >
                  <p className={`text-xs font-bold ${isToday ? "text-indigo-600" : "text-gray-700"}`}>{day}</p>
                  {summary ? (
                    <div className="flex justify-center gap-0.5 mt-1">
                      {summary.present > 0 && <span className="w-2 h-2 rounded-full bg-green-500" title={`${summary.present} present`} />}
                      {summary.absent > 0 && <span className="w-2 h-2 rounded-full bg-red-500" title={`${summary.absent} absent`} />}
                      {summary.perm > 0 && <span className="w-2 h-2 rounded-full bg-violet-500" title={`${summary.perm} permission`} />}
                    </div>
                  ) : <div className="h-3" />}
                </button>
              );
            })}
          </div>

          {/* Selected Day Detail */}
          {selectedDate && (
            <Card className="p-3 border border-indigo-200 bg-white" data-testid="day-detail">
              <h4 className="text-sm font-bold text-gray-900 mb-2">
                {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
              </h4>
              {dayRecords.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No attendance recorded</p>
              ) : (
                <div className="space-y-2">
                  {dayRecords.map(r => (
                    <div key={r.attendance_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600">{r.employee_name?.charAt(0)}</div>
                        <div>
                          <p className="text-xs font-bold text-gray-900">{r.employee_name}</p>
                          <div className="flex items-center gap-1 text-[10px] text-gray-500">
                            {r.check_in && <span className="text-green-600">In:{r.check_in}</span>}
                            {r.check_out && <span className="text-red-600">Out:{r.check_out}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <StatusBadge status={r.status} />
                        <Button size="sm" variant="ghost" onClick={() => handleEditDay(r)} className="h-6 w-6 p-0"><PencilSimple size={12} className="text-indigo-600" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteDay(r.attendance_id)} className="h-6 w-6 p-0"><Trash size={12} className="text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Edit Dialog for Calendar */}
      <Dialog open={editRecord !== null} onOpenChange={() => setEditRecord(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="text-lg font-bold">Edit Attendance</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-semibold text-gray-700">{editRecord?.employee_name} — {editRecord?.date}</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs font-semibold">Check In</Label><Input type="time" value={editForm.check_in} onChange={(e) => setEditForm(p => ({ ...p, check_in: e.target.value }))} className="h-10" /></div>
              <div><Label className="text-xs font-semibold">Check Out</Label><Input type="time" value={editForm.check_out} onChange={(e) => setEditForm(p => ({ ...p, check_out: e.target.value }))} className="h-10" /></div>
            </div>
            <div><Label className="text-xs font-semibold">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(p => ({ ...p, status: v }))}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem><SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem><SelectItem value="leave">Leave</SelectItem>
                  <SelectItem value="permission">Permission</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.status === "permission" && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs font-semibold">Hours</Label><Input type="number" min="0.5" max="8" step="0.5" value={editForm.permission_hours} onChange={(e) => setEditForm(p => ({ ...p, permission_hours: parseFloat(e.target.value) || 0 }))} className="h-10" /></div>
                <div><Label className="text-xs font-semibold">Reason</Label><Input value={editForm.permission_reason} onChange={(e) => setEditForm(p => ({ ...p, permission_reason: e.target.value }))} className="h-10" /></div>
              </div>
            )}
            <Button onClick={saveEdit} className="w-full h-10 bg-green-600 hover:bg-green-700 text-white font-bold"><Check size={16} className="mr-1" /> Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====== Bulk Entry ====== */
function BulkEntryView({ teamMembers, onDone }) {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [entries, setEntries] = useState({});
  const [saving, setSaving] = useState(false);

  const getDates = () => {
    const dates = [];
    const start = new Date(dateFrom + "T00:00:00");
    const end = new Date(dateTo + "T00:00:00");
    const d = new Date(start);
    while (d <= end) {
      dates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
      d.setDate(d.getDate() + 1);
    }
    return dates;
  };

  const dates = getDates();

  const getEntry = (emp, date) => entries[`${emp}|${date}`] || "present";
  const setEntry = (emp, date, status) => setEntries(prev => ({ ...prev, [`${emp}|${date}`]: status }));

  const setAllForDate = (date, status) => {
    setEntries(prev => {
      const updated = { ...prev };
      teamMembers.forEach(m => { updated[`${m.name}|${date}`] = status; });
      return updated;
    });
  };

  const handleSave = async () => {
    const bulkEntries = [];
    for (const date of dates) {
      for (const m of teamMembers) {
        const status = getEntry(m.name, date);
        bulkEntries.push({ employee_name: m.name, date, status });
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/attendance/bulk`, { entries: bulkEntries });
      toast.success(`${res.data.saved} entries saved, ${res.data.skipped} skipped (already exist)`);
      if (onDone) onDone();
    } catch (err) { toast.error("Bulk save failed"); }
    finally { setSaving(false); }
  };

  const statusOptions = [
    { value: "present", label: "P", color: "bg-green-500 text-white" },
    { value: "absent", label: "A", color: "bg-red-500 text-white" },
    { value: "half_day", label: "H", color: "bg-amber-500 text-white" },
    { value: "leave", label: "L", color: "bg-blue-500 text-white" },
    { value: "permission", label: "Pm", color: "bg-violet-500 text-white" },
  ];

  return (
    <div data-testid="bulk-entry">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex-1"><Label className="text-xs font-semibold mb-1 block">From Date</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" data-testid="bulk-from" /></div>
        <div className="flex-1"><Label className="text-xs font-semibold mb-1 block">To Date</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10" data-testid="bulk-to" /></div>
      </div>

      {dates.length > 31 ? (
        <p className="text-sm text-red-500 font-semibold">Max 31 days range. Please adjust dates.</p>
      ) : dates.length === 0 ? (
        <p className="text-sm text-gray-400">Select a valid date range</p>
      ) : (
        <>
          <div className="overflow-x-auto mobile-scroll-x mb-3">
            <table className="w-full text-xs border-collapse" data-testid="bulk-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="sticky left-0 bg-gray-50 px-2 py-2 text-left font-semibold text-gray-600 border-b z-10 min-w-[120px]">Employee</th>
                  {dates.map(d => (
                    <th key={d} className="px-1 py-2 text-center font-medium text-gray-500 border-b min-w-[44px]">
                      <div className="text-[10px]">{d.split("-")[2]}/{d.split("-")[1]}</div>
                      <button onClick={() => setAllForDate(d, "present")} className="text-[8px] text-green-600 font-bold hover:underline mt-0.5">All P</button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teamMembers.map(m => (
                  <tr key={m.name} className="border-b hover:bg-gray-50/50">
                    <td className="sticky left-0 bg-white px-2 py-1.5 font-semibold text-gray-900 whitespace-nowrap z-10">{m.name}</td>
                    {dates.map(d => {
                      const current = getEntry(m.name, d);
                      const cfg = statusOptions.find(s => s.value === current);
                      return (
                        <td key={d} className="px-1 py-1 text-center">
                          <button
                            onClick={() => {
                              const idx = statusOptions.findIndex(s => s.value === current);
                              const next = statusOptions[(idx + 1) % statusOptions.length];
                              setEntry(m.name, d, next.value);
                            }}
                            className={`w-8 h-8 rounded-lg text-[10px] font-bold ${cfg?.color || "bg-green-500 text-white"} touch-target`}
                            data-testid={`bulk-${m.name}-${d}`}
                          >
                            {cfg?.label || "P"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {statusOptions.map(s => (
                <span key={s.value} className={`px-2 py-0.5 rounded text-[10px] font-bold ${s.color}`}>{s.label} = {STATUS_CONFIG[s.value]?.label}</span>
              ))}
            </div>
            <Button onClick={handleSave} disabled={saving} className="h-10 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="bulk-save-btn">
              <Check size={16} className="mr-1" /> {saving ? "Saving..." : `Save All (${dates.length * teamMembers.length})`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/* ====== Salary View ====== */
function SalaryView({ teamMembers }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [salaryData, setSalaryData] = useState(null);
  const [salaryEntries, setSalaryEntries] = useState([]);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualForm, setManualForm] = useState({ employee_name: "", salary_amount: "", advance: "", deductions: "", bonus: "", extra_allowance: "" });

  const fetchSalaryEntries = useCallback(async () => {
    try { const r = await axios.get(`${API}/salary-entries`, { params: { year, month } }); setSalaryEntries(r.data); } catch (err) { toast.error("Failed to load salary entries"); }
  }, [year, month]);

  useEffect(() => { fetchSalaryEntries(); }, [fetchSalaryEntries]);

  const calcSalary = async () => {
    if (!selectedEmp) { toast.error("Select employee"); return; }
    try {
      const r = await axios.get(`${API}/attendance/salary-calc`, { params: { year, month, employee_name: selectedEmp } });
      setSalaryData(r.data);
    } catch (err) { toast.error("Calculation failed"); }
  };

  const saveManualEntry = async () => {
    if (!manualForm.employee_name) { toast.error("Select employee"); return; }
    if (!manualForm.salary_amount || parseFloat(manualForm.salary_amount) <= 0) { toast.error("Enter salary amount"); return; }
    try {
      await axios.post(`${API}/salary-entries`, {
        employee_name: manualForm.employee_name, year, month,
        salary_amount: parseFloat(manualForm.salary_amount) || 0,
        advance: parseFloat(manualForm.advance) || 0,
        deductions: parseFloat(manualForm.deductions) || 0,
        bonus: parseFloat(manualForm.bonus) || 0,
        extra_allowance: parseFloat(manualForm.extra_allowance) || 0,
      });
      toast.success("Salary entry saved!");
      setShowManualEntry(false);
      setManualForm({ employee_name: "", salary_amount: "", advance: "", deductions: "", bonus: "", extra_allowance: "" });
      fetchSalaryEntries();
      if (selectedEmp) calcSalary();
    } catch (err) { toast.error("Failed to save"); }
  };

  const grossAmount = (parseFloat(manualForm.salary_amount) || 0) + (parseFloat(manualForm.bonus) || 0) + (parseFloat(manualForm.extra_allowance) || 0);
  const totalDeduct = (parseFloat(manualForm.advance) || 0) + (parseFloat(manualForm.deductions) || 0);
  const manualFinal = grossAmount - totalDeduct;

  const downloadCSV = () => { window.open(`${API}/salary-export?year=${year}&month=${month}`, "_blank"); };

  const printSlip = () => {
    if (!salaryData) { toast.error("Calculate salary first"); return; }
    const d = salaryData;
    const mn = MONTH_NAMES[month - 1];
    const html = `<!DOCTYPE html><html><head><title>Salary Slip - ${d.employee_name}</title>
      <style>body{font-family:Arial,sans-serif;max-width:700px;margin:20px auto;padding:20px;color:#333}
      h1{text-align:center;color:#4338ca;margin-bottom:4px}h2{text-align:center;color:#666;font-weight:normal;font-size:14px;margin-top:0}
      .header{border-bottom:2px solid #4338ca;padding-bottom:10px;margin-bottom:15px}
      table{width:100%;border-collapse:collapse;margin:10px 0}td,th{padding:8px 12px;border:1px solid #e5e7eb;text-align:left;font-size:13px}
      th{background:#f3f4f6;font-weight:600}.right{text-align:right}.bold{font-weight:700}
      .total-row{background:#eef2ff;font-weight:700;font-size:15px}.green{color:#16a34a}.red{color:#dc2626}.blue{color:#4338ca}
      .footer{text-align:center;margin-top:30px;color:#999;font-size:11px}
      @media print{body{margin:0;padding:15px}}</style></head><body>
      <div class="header"><h1>Huma Spices</h1><h2>Salary Slip - ${mn} ${year}</h2></div>
      <table><tr><th>Employee</th><td class="bold">${d.employee_name}</td><th>Month</th><td>${mn} ${year}</td></tr></table>
      <h3 style="margin:15px 0 5px;font-size:14px">Attendance Summary</h3>
      <table><tr><th>Present Days</th><td class="right green bold">${d.present_days}</td><th>Absent Days</th><td class="right red bold">${d.absent_days}</td></tr>
      <tr><th>Half Days</th><td class="right">${d.half_days}</td><th>Leave Days</th><td class="right">${d.leave_days}</td></tr>
      <tr><th>Permission Hours</th><td class="right">${d.permission_hours}h</td><th>Total Hours</th><td class="right">${d.total_hours}h</td></tr></table>
      <h3 style="margin:15px 0 5px;font-size:14px">Salary Breakdown</h3>
      <table><tr><th>Gross Salary</th><td class="right bold">${formatCurrency(d.salary_entered)}</td></tr>
      ${d.bonus > 0 ? `<tr><td>Bonus</td><td class="right green">+${formatCurrency(d.bonus)}</td></tr>` : ""}
      ${d.extra_allowance > 0 ? `<tr><td>Extra Allowance</td><td class="right green">+${formatCurrency(d.extra_allowance)}</td></tr>` : ""}
      ${d.breakdown ? `<tr><td>Absent Deduction (${d.absent_days}d)</td><td class="right red">-${formatCurrency(d.breakdown.absent_deduction)}</td></tr>
      <tr><td>Half Day Deduction (${d.half_days}d)</td><td class="right red">-${formatCurrency(d.breakdown.halfday_deduction)}</td></tr>
      <tr><td>Permission Deduction (${d.permission_hours}h)</td><td class="right red">-${formatCurrency(d.breakdown.permission_deduction)}</td></tr>` : ""}
      ${d.advance > 0 ? `<tr><td>Advance Deduction</td><td class="right red">-${formatCurrency(d.advance)}</td></tr>` : ""}
      ${d.deductions > 0 ? `<tr><td>Other Deductions</td><td class="right red">-${formatCurrency(d.deductions)}</td></tr>` : ""}
      <tr class="total-row"><th>Net Payable Salary</th><td class="right blue">${formatCurrency(d.salary_payable)}</td></tr></table>
      <div class="footer"><p>Generated on ${new Date().toLocaleDateString("en-IN")}</p><p>This is a system-generated salary slip</p></div>
      </body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 300);
  };

  return (
    <div data-testid="salary-view">
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Select value={selectedEmp} onValueChange={setSelectedEmp}>
          <SelectTrigger className="h-10 text-sm flex-1" data-testid="salary-employee"><SelectValue placeholder="Select Employee" /></SelectTrigger>
          <SelectContent>{teamMembers.map(m => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={`${month}`} onValueChange={(v) => setMonth(parseInt(v))}>
          <SelectTrigger className="h-10 text-sm w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={m} value={`${i + 1}`}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="h-10 w-[90px] text-sm" />
        <Button onClick={calcSalary} className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold" data-testid="calc-salary-btn">
          <CurrencyInr size={16} className="mr-1" /> Calculate
        </Button>
      </div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <Button variant="outline" onClick={() => { setManualForm({ employee_name: "", salary_amount: "", advance: "", deductions: "", bonus: "", extra_allowance: "" }); setShowManualEntry(true); }} className="h-9 text-xs font-bold" data-testid="manual-salary-btn">
          <CurrencyInr size={14} className="mr-1" /> Enter Monthly Salary
        </Button>
        <Button variant="outline" onClick={downloadCSV} className="h-9 text-xs" data-testid="export-salary-btn">
          <DownloadSimple size={14} className="mr-1" /> Export CSV
        </Button>
        {salaryData && (
          <Button variant="outline" onClick={printSlip} className="h-9 text-xs" data-testid="print-salary-btn">
            <DownloadSimple size={14} className="mr-1" /> Print Slip
          </Button>
        )}
      </div>

      {salaryData && (
        <Card className="p-4 border border-gray-200 bg-white mb-3" data-testid="salary-result">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-900">{salaryData.employee_name} — {MONTH_NAMES[month - 1]} {year}</h4>
            {salaryData.has_manual_entry && <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold border border-violet-200">Manual Entry</span>}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
            <div className="bg-green-50 rounded-lg p-2 text-center"><p className="text-[10px] text-gray-500">Present</p><p className="text-lg font-bold text-green-600">{salaryData.present_days}</p></div>
            <div className="bg-red-50 rounded-lg p-2 text-center"><p className="text-[10px] text-gray-500">Absent</p><p className="text-lg font-bold text-red-600">{salaryData.absent_days}</p></div>
            <div className="bg-amber-50 rounded-lg p-2 text-center"><p className="text-[10px] text-gray-500">Half Days</p><p className="text-lg font-bold text-amber-600">{salaryData.half_days}</p></div>
            <div className="bg-blue-50 rounded-lg p-2 text-center"><p className="text-[10px] text-gray-500">Leave</p><p className="text-lg font-bold text-blue-600">{salaryData.leave_days}</p></div>
            <div className="bg-violet-50 rounded-lg p-2 text-center"><p className="text-[10px] text-gray-500">Permission</p><p className="text-lg font-bold text-violet-600">{salaryData.permission_hours}h</p></div>
            <div className="bg-gray-50 rounded-lg p-2 text-center"><p className="text-[10px] text-gray-500">Total Hours</p><p className="text-lg font-bold text-gray-700">{salaryData.total_hours}h</p></div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-1.5 text-xs" data-testid="salary-breakdown">
            <div className="flex justify-between"><span className="text-gray-500">Gross Salary</span><span className="font-bold">{formatCurrency(salaryData.salary_entered)}</span></div>
            {salaryData.breakdown && <div className="flex justify-between"><span className="text-gray-500">Daily Rate (÷30)</span><span className="font-mono">{formatCurrency(salaryData.breakdown.daily_rate)}</span></div>}
            {(salaryData.bonus > 0 || salaryData.extra_allowance > 0) && <hr className="border-gray-200" />}
            {salaryData.bonus > 0 && <div className="flex justify-between text-green-600"><span>Bonus</span><span className="font-mono">+{formatCurrency(salaryData.bonus)}</span></div>}
            {salaryData.extra_allowance > 0 && <div className="flex justify-between text-green-600"><span>Extra Allowance</span><span className="font-mono">+{formatCurrency(salaryData.extra_allowance)}</span></div>}
            <hr className="border-gray-200" />
            {salaryData.breakdown && salaryData.breakdown.absent_deduction > 0 && <div className="flex justify-between text-red-600"><span>Absent ({salaryData.absent_days}d)</span><span className="font-mono">-{formatCurrency(salaryData.breakdown.absent_deduction)}</span></div>}
            {salaryData.breakdown && salaryData.breakdown.halfday_deduction > 0 && <div className="flex justify-between text-amber-600"><span>Half Day ({salaryData.half_days}d x 50%)</span><span className="font-mono">-{formatCurrency(salaryData.breakdown.halfday_deduction)}</span></div>}
            {salaryData.breakdown && salaryData.breakdown.permission_deduction > 0 && <div className="flex justify-between text-violet-600"><span>Permission ({salaryData.permission_hours}h / 8h)</span><span className="font-mono">-{formatCurrency(salaryData.breakdown.permission_deduction)}</span></div>}
            {salaryData.advance > 0 && <div className="flex justify-between text-orange-600"><span>Advance Deduction</span><span className="font-mono">-{formatCurrency(salaryData.advance)}</span></div>}
            {salaryData.deductions > 0 && <div className="flex justify-between text-red-600"><span>Other Deductions</span><span className="font-mono">-{formatCurrency(salaryData.deductions)}</span></div>}
            <hr className="border-gray-200" />
            {salaryData.breakdown && <div className="flex justify-between font-bold text-red-600"><span>Total Deductions</span><span className="font-mono">-{formatCurrency(salaryData.breakdown.total_deduction + (salaryData.advance || 0))}</span></div>}
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 font-semibold">Net Payable Salary</p>
              <p className="text-2xl font-black text-indigo-700" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="net-salary">{formatCurrency(salaryData.salary_payable)}</p>
            </div>
          </div>
          {!salaryData.config && !salaryData.has_manual_entry && <p className="text-xs text-amber-600 mt-2 font-medium">No salary config found. Use "Enter Monthly Salary" or configure salary first.</p>}
        </Card>
      )}

      {salaryEntries.length > 0 && (
        <Card className="p-3 border border-gray-200 bg-white mb-3" data-testid="salary-entries-table">
          <h4 className="text-sm font-bold text-gray-900 mb-2">Salary Entries — {MONTH_NAMES[month - 1]} {year}</h4>
          <div className="space-y-2 sm:hidden">
            {salaryEntries.map((e) => (
              <div key={e.employee_name} className="p-3 bg-gray-50 rounded-lg">
                <p className="font-bold text-sm text-gray-900">{e.employee_name}</p>
                <div className="flex justify-between mt-1 text-xs">
                  <span>Salary: {formatCurrency(e.salary_amount)}</span>
                  <span className="font-bold text-indigo-700">Net: {formatCurrency(e.final_salary)}</span>
                </div>
                {(e.bonus > 0 || e.extra_allowance > 0) && <div className="text-[10px] text-green-600 mt-0.5">{e.bonus > 0 ? `Bonus: ${formatCurrency(e.bonus)}` : ""} {e.extra_allowance > 0 ? `Allow: ${formatCurrency(e.extra_allowance)}` : ""}</div>}
              </div>
            ))}
          </div>
          <div className="overflow-x-auto hidden sm:block">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Employee</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Salary</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Bonus</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Allowance</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Advance</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Deductions</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Net Pay</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {salaryEntries.map((e) => (
                  <tr key={e.employee_name} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-semibold text-gray-900">{e.employee_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatCurrency(e.salary_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-green-600">{e.bonus > 0 ? `+${formatCurrency(e.bonus)}` : "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-green-600">{e.extra_allowance > 0 ? `+${formatCurrency(e.extra_allowance)}` : "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-orange-600">{e.advance > 0 ? `-${formatCurrency(e.advance)}` : "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{e.deductions > 0 ? `-${formatCurrency(e.deductions)}` : "-"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-indigo-700">{formatCurrency(e.final_salary)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={showManualEntry} onOpenChange={setShowManualEntry}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle className="text-lg font-bold">Enter Monthly Salary</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-gray-500">Manual entry for <b>{MONTH_NAMES[month - 1]} {year}</b></p>
            <div><Label className="text-sm font-semibold">Employee</Label>
              <Select value={manualForm.employee_name} onValueChange={(v) => setManualForm(p => ({ ...p, employee_name: v }))}>
                <SelectTrigger className="h-10" data-testid="manual-salary-employee"><SelectValue placeholder="Select Employee" /></SelectTrigger>
                <SelectContent>{teamMembers.map(m => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-semibold">Monthly Salary Amount</Label>
              <Input type="number" value={manualForm.salary_amount} onChange={(e) => setManualForm(p => ({ ...p, salary_amount: e.target.value }))} className="h-10" placeholder="e.g. 24200" data-testid="manual-salary-amount" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-sm font-semibold text-green-700">Bonus</Label><Input type="number" value={manualForm.bonus} onChange={(e) => setManualForm(p => ({ ...p, bonus: e.target.value }))} className="h-10 border-green-200" placeholder="0" data-testid="manual-bonus" /></div>
              <div><Label className="text-sm font-semibold text-green-700">Extra Allowance</Label><Input type="number" value={manualForm.extra_allowance} onChange={(e) => setManualForm(p => ({ ...p, extra_allowance: e.target.value }))} className="h-10 border-green-200" placeholder="0" data-testid="manual-extra-allowance" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-sm font-semibold text-orange-700">Advance Deduction</Label><Input type="number" value={manualForm.advance} onChange={(e) => setManualForm(p => ({ ...p, advance: e.target.value }))} className="h-10 border-orange-200" placeholder="0" data-testid="manual-salary-advance" /></div>
              <div><Label className="text-sm font-semibold text-red-700">Other Deductions</Label><Input type="number" value={manualForm.deductions} onChange={(e) => setManualForm(p => ({ ...p, deductions: e.target.value }))} className="h-10 border-red-200" placeholder="0" data-testid="manual-salary-deductions" /></div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-xs">
              <div className="flex justify-between"><span>Gross Salary</span><span className="font-bold">{formatCurrency(parseFloat(manualForm.salary_amount) || 0)}</span></div>
              {(parseFloat(manualForm.bonus) || 0) > 0 && <div className="flex justify-between text-green-600"><span>+ Bonus</span><span>+{formatCurrency(parseFloat(manualForm.bonus))}</span></div>}
              {(parseFloat(manualForm.extra_allowance) || 0) > 0 && <div className="flex justify-between text-green-600"><span>+ Allowance</span><span>+{formatCurrency(parseFloat(manualForm.extra_allowance))}</span></div>}
              {(parseFloat(manualForm.advance) || 0) > 0 && <div className="flex justify-between text-orange-600"><span>- Advance</span><span>-{formatCurrency(parseFloat(manualForm.advance))}</span></div>}
              {(parseFloat(manualForm.deductions) || 0) > 0 && <div className="flex justify-between text-red-600"><span>- Deductions</span><span>-{formatCurrency(parseFloat(manualForm.deductions))}</span></div>}
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Net Payable</span>
              <span className="text-xl font-black text-indigo-700" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="manual-salary-final">{formatCurrency(Math.max(manualFinal, 0))}</span>
            </div>
            <Button onClick={saveManualEntry} className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-manual-salary-btn"><Check size={16} className="mr-1" /> Save Salary Entry</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====== Main Page ====== */
export default function AttendancePage() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [tab, setTab] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchTeam = useCallback(async () => {
    try { const r = await axios.get(`${API}/team`); setTeamMembers(r.data.filter(m => m.status === "Active")); } catch (err) { toast.error("Failed to load team"); }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try { const r = await axios.get(`${API}/attendance/dashboard`); setDashboard(r.data); } catch (err) { toast.error("Failed to load dashboard"); }
  }, []);

  useEffect(() => { fetchTeam(); fetchDashboard(); }, [fetchTeam, fetchDashboard]);

  const handleCheckout = async (id) => {
    try { await axios.post(`${API}/attendance/checkout/${id}`); toast.success("Checked out!"); fetchDashboard(); } catch (err) { toast.error("Checkout failed"); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await axios.delete(`${API}/attendance/${id}`); toast.success("Deleted!"); fetchDashboard(); } catch (err) { toast.error("Delete failed"); }
  };
  const handleEdit = (record) => {
    setEditRecord(record);
    setEditForm({ check_in: record.check_in || "", check_out: record.check_out || "", status: record.status, notes: record.notes || "", permission_hours: record.permission_hours || 0, permission_reason: record.permission_reason || "" });
  };
  const saveEdit = async () => {
    try { await axios.put(`${API}/attendance/${editRecord.attendance_id}`, editForm); toast.success("Updated!"); setEditRecord(null); fetchDashboard(); } catch (err) { toast.error("Update failed"); }
  };

  const tabs = [
    { key: "today", label: "Today", icon: Clock },
    { key: "calendar", label: "Calendar", icon: CalendarDots },
    { key: "bulk", label: "Bulk Entry", icon: UsersThree },
    { key: "salary", label: "Salary", icon: CurrencyInr },
  ];

  return (
    <div className="space-y-4 max-w-7xl" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Attendance Register</h1>
          <p className="text-xs sm:text-sm font-bold text-indigo-600">{dashboard?.today ? new Date(dashboard.today + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : ""}{statusFilter !== "all" && <span className="ml-2 text-amber-600">· Filter: {statusFilter}</span>}{statusFilter !== "all" && <button onClick={() => setStatusFilter("all")} className="ml-2 text-[10px] underline text-red-500">clear</button>}</p>
        </div>
      </div>

      {dashboard && <DashboardCards data={dashboard} onCardClick={(k) => { setTab("today"); setStatusFilter(k); }} />}
      <QuickCheckIn teamMembers={teamMembers} onDone={fetchDashboard} />

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto mobile-scroll-x" data-testid="attendance-tabs">
        {tabs.map((t) => (
          <Button key={t.key} size="sm" variant={tab === t.key ? "default" : "ghost"}
            className={`flex-1 h-9 text-xs font-bold whitespace-nowrap ${tab === t.key ? "bg-indigo-600 text-white" : "text-gray-600"}`}
            onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
          >
            <t.icon size={14} className="mr-1" /> {t.label}
          </Button>
        ))}
      </div>

      {tab === "today" && dashboard && <TodayList records={(dashboard.today_records || []).filter(r => statusFilter === "all" || r.status === statusFilter)} onCheckout={handleCheckout} onEdit={handleEdit} onDelete={handleDelete} />}
      {tab === "calendar" && <CalendarView teamMembers={teamMembers} onRefresh={fetchDashboard} />}
      {tab === "bulk" && <BulkEntryView teamMembers={teamMembers} onDone={() => { fetchDashboard(); setTab("today"); }} />}
      {tab === "salary" && <SalaryView teamMembers={teamMembers} />}

      {/* Edit Dialog for Today tab */}
      <Dialog open={editRecord !== null && tab === "today"} onOpenChange={() => setEditRecord(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle className="text-lg font-bold">Edit Attendance</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-semibold text-gray-700">{editRecord?.employee_name} — {editRecord?.date}</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs font-semibold">Check In</Label><Input type="time" value={editForm.check_in} onChange={(e) => setEditForm(p => ({ ...p, check_in: e.target.value }))} className="h-10" data-testid="edit-checkin" /></div>
              <div><Label className="text-xs font-semibold">Check Out</Label><Input type="time" value={editForm.check_out} onChange={(e) => setEditForm(p => ({ ...p, check_out: e.target.value }))} className="h-10" data-testid="edit-checkout" /></div>
            </div>
            <div><Label className="text-xs font-semibold">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(p => ({ ...p, status: v }))}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem><SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem><SelectItem value="leave">Leave</SelectItem>
                  <SelectItem value="permission">Permission</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.status === "permission" && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs font-semibold">Hours</Label><Input type="number" min="0.5" max="8" step="0.5" value={editForm.permission_hours} onChange={(e) => setEditForm(p => ({ ...p, permission_hours: parseFloat(e.target.value) || 0 }))} className="h-10" data-testid="edit-permission-hours" /></div>
                <div><Label className="text-xs font-semibold">Reason</Label><Input value={editForm.permission_reason} onChange={(e) => setEditForm(p => ({ ...p, permission_reason: e.target.value }))} className="h-10" data-testid="edit-permission-reason" /></div>
              </div>
            )}
            <div><Label className="text-xs font-semibold">Notes</Label><Input value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} className="h-10" placeholder="Optional notes..." /></div>
            <Button onClick={saveEdit} className="w-full h-10 bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="save-edit-btn"><Check size={16} className="mr-1" /> Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
